import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router, UrlTree } from '@angular/router';
import { catchError, filter, map, Observable, of, take, timeout } from 'rxjs';
import { ConfigService } from '../../config/services/config.service';
import { SplashScreenStateService } from '../../common/services/splash-screen-state.service';
import { LoggingService } from '../../logging/logging.service';
import { isArrivalBoardEnabled } from '../arrival-board-enabled';

/** How long to wait for the authenticated configuration before deciding on whatever is cached. */
export const ARRIVAL_BOARD_CONFIG_WAIT_MS = 10_000;

/**
 * Gated by {@link isArrivalBoardEnabled} — the AmbER stack plus the tenant's `features.arrivalBoard.enabled` — not by a
 * white-label build config.
 *
 * ⚠ Unlike the other feature guards it waits for {@link ConfigService.authenticatedConfigLoaded}: a kiosk
 * opened cold by URL may otherwise see only the public or a stale cached config and be redirected wrongly.
 */
export const arrivalBoardFeatureGuard = (): boolean | UrlTree | Observable<boolean | UrlTree> => {
    const router = inject(Router);
    const configService = inject(ConfigService);
    const splashScreenStateService = inject(SplashScreenStateService);
    const log = inject(LoggingService).getLogger('arrivalBoardFeatureGuard');

    const decide = (): boolean | UrlTree => {
        if (isArrivalBoardEnabled(configService.config()?.configuration?.features)) {
            splashScreenStateService.stop();
            return true;
        }
        log.info("Feature arrivalBoard isn't enabled");
        return router.createUrlTree(['/']);
    };

    if (configService.authenticatedConfigLoaded()) return decide();

    // `ConfigService.init()` is not awaited at startup (app.config.ts), so a kiosk opened cold by URL would
    // otherwise be decided on no, the public or a stale cached config — and redirected to `/` for good.
    return toObservable(configService.authenticatedConfigLoaded).pipe(
        filter((loaded) => loaded),
        take(1),
        timeout(ARRIVAL_BOARD_CONFIG_WAIT_MS),
        catchError(() => {
            log.warn(`configuration not loaded after ${ARRIVAL_BOARD_CONFIG_WAIT_MS} ms, deciding on what is cached`);
            return of(true);
        }),
        map(() => decide()),
    );
};
